import type { ReactNode } from 'react'
import { MedalIcon } from '../ui/dashIcons'

/**
 * People, ranks and priorities — the three shapes every adult tier drew by hand.
 *
 * 🔴 WHY THIS FILE EXISTS: 👧 AND 👦, ASSIGNED BY GUESSING AT A NAME.
 *
 * The teacher's roster carried a function called `getStudentAvatarIcon` whose
 * body was a list of seven first names — "amaira", "zara", "meera", "diya" —
 * that returned 👧, and 👦 for everybody else. So the product looked at a
 * four-year-old's name and asserted their gender on screen, wrongly for every
 * child outside that list and unknowably for most inside it, in a register a
 * teacher reads out. The school tier had already solved the same slot properly
 * with initials in a tinted disc; the teacher tier had not.
 *
 * `Avatar` is that solution, once. It draws initials. It knows nothing about
 * anybody and asserts nothing about them.
 *
 * ⚠️ THE TONE IS DERIVED FROM THE NAME, AND IT IS IDENTITY RATHER THAN STATUS
 * (§12). It exists so a teacher scanning thirty rows can find the same child
 * twice in the same colour — not to say anything about how they are doing. A
 * value-derived tone here would be a grade drawn on a person.
 */

export type PersonTone = 'primary' | 'teal' | 'leaf' | 'indigo' | 'sunshine' | 'coral'

const PERSON_TONES: readonly PersonTone[] = [
  'primary',
  'teal',
  'leaf',
  'indigo',
  'sunshine',
  'coral',
]

const TONE_FILL: Record<PersonTone, string> = {
  primary: 'var(--color-primary)',
  teal: 'var(--color-teal)',
  leaf: 'var(--color-leaf)',
  indigo: 'var(--color-indigo)',
  sunshine: 'var(--color-sunshine)',
  coral: 'var(--color-coral)',
}

/**
 * A stable tone for a name.
 *
 * ⚠️ SUM OF CODE UNITS, NOT `Math.random` OR AN INDEX. An index-derived tone
 * changes when the list is sorted or filtered, so the same child is a different
 * colour on the roster and on the leaderboard — which defeats the only reason
 * the colour is there.
 */
export function personTone(name: string): PersonTone {
  let sum = 0
  for (let i = 0; i < name.length; i += 1) sum += name.charCodeAt(i)
  return PERSON_TONES[sum % PERSON_TONES.length] ?? 'primary'
}

/**
 * One or two letters from a name.
 *
 * ⚠️ IT TAKES THE FIRST LETTER OF THE FIRST AND LAST WORD, and falls back to
 * one letter rather than to a placeholder glyph. A child on the register with
 * a single name is the common case in the schools this ships to, not an edge.
 */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '?'
  const first = words[0]?.[0] ?? ''
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase()
}

export interface AvatarProps {
  name: string
  /** 28 in a dense table row, 40 on a card, 64 on a profile header. */
  size?: number
  /** Overrides the name-derived tone. Use only where the row already has one. */
  tone?: PersonTone
}

export function Avatar({ name, size = 40, tone }: AvatarProps) {
  const fill = TONE_FILL[tone ?? personTone(name)]
  return (
    <span
      aria-hidden="true"
      className="tabular flex shrink-0 items-center justify-center rounded-full border font-dash font-extrabold"
      style={{
        width: size,
        height: size,
        /* A tint of the tone, never the tone itself — the text on top has to
           stay readable, and §"no grey" means the fallback is warm. */
        background: `color-mix(in srgb, ${fill} 14%, var(--color-surface))`,
        borderColor: `color-mix(in srgb, ${fill} 28%, var(--color-line))`,
        color: fill,
        fontSize: Math.round(size * 0.36),
      }}
    >
      {initialsOf(name)}
    </span>
  )
}

/**
 * A leaderboard place.
 *
 * 🔴 THIS REPLACED 🥇🥈🥉, AND THE NUMERAL IS WHY. Three medal emoji made the
 * place readable by colour and shape alone — §12's rule is that status is icon
 * PLUS colour PLUS text, and "🥈" carries no text at all. Now every row prints
 * its rank as a number; the top three additionally get a medal outline and a
 * tone, which is decoration over a fact rather than instead of one.
 *
 * ⚠️ ONE MEDAL SHAPE FOR ALL THREE. Gold, silver and bronze are the tones.
 */
export function RankBadge({ rank }: { rank: number }) {
  const podium =
    rank === 1
      ? { fill: 'var(--color-sunshine)', label: 'var(--color-warning)' }
      : rank === 2
        ? { fill: 'var(--color-teal)', label: 'var(--color-teal)' }
        : rank === 3
          ? { fill: 'var(--color-coral)', label: 'var(--color-coral)' }
          : null

  if (podium === null) {
    return (
      <span className="tabular pl-2 font-dash text-dash-xs font-bold text-muted">
        {rank}
      </span>
    )
  }

  return (
    <span
      className="tabular inline-flex items-center gap-1 rounded-pill border px-2.5 py-0.5 font-dash text-dash-xs font-extrabold"
      style={{
        background: `color-mix(in srgb, ${podium.fill} 15%, var(--color-surface))`,
        borderColor: `color-mix(in srgb, ${podium.fill} 34%, var(--color-line))`,
        color: podium.label,
      }}
    >
      <MedalIcon size={13} strokeWidth={2.4} />
      {rank}
    </span>
  )
}

export type PriorityTone = 'attention' | 'waiting' | 'good'

const PRIORITY: Record<PriorityTone, { fill: string; fg: string }> = {
  /* Coral, not danger red. §3 reserves `danger` for a destructive act; a child
     who has not played for eight days is a thing to notice, not an error. */
  attention: { fill: 'var(--color-coral)', fg: 'var(--color-danger)' },
  waiting: { fill: 'var(--color-indigo)', fg: 'var(--color-indigo)' },
  good: { fill: 'var(--color-leaf)', fg: 'var(--color-success)' },
}

export interface PriorityCardProps {
  title: ReactNode
  count: number
  tone: PriorityTone
  icon: ReactNode
  /** Drawn under the heading, above the rows. */
  hint?: ReactNode
  /** The rows themselves, or the empty sentence. */
  children: ReactNode
  /** One action, bottom-right. Omitted rather than drawn inert. */
  action?: { label: ReactNode; onClick: () => void }
}

/**
 * One of the teacher's three priority columns.
 *
 * 🔴 THREE, NOT TWO, AND THE THIRD IS THE POINT. D4 shipped with "Needs
 * attention" and "Not started" side by side and nothing else — so a class where
 * twenty of twenty-seven children are doing well was drawn as two lists of
 * problems and no mention of the twenty. A teacher opening that every morning
 * reads their class as a queue of failures. "Going well" is the same data
 * subtracted, and it costs one derived count.
 *
 * ⚠️ THE COUNT IS IN A PILL BESIDE THE HEADING, NOT INSIDE IT. It updates; the
 * heading does not, and a number that moves inside a sentence is the thing that
 * makes a page feel like it is flickering.
 *
 * ⚠️ `action` IS OPTIONAL AND IS NEVER DRAWN WITHOUT A HANDLER. The two "View
 * all (4) ∨" buttons that used to sit at the foot of these panels had empty
 * `onClick`s — a control that looks live and does nothing, which is worse than
 * no control because it costs the reader a click to learn it is dead.
 */
export function PriorityCard({
  title,
  count,
  tone,
  icon,
  hint,
  children,
  action,
}: PriorityCardProps) {
  const t = PRIORITY[tone]
  return (
    <div
      className="flex flex-col rounded-lg border bg-surface p-4 shadow-card sm:p-5"
      style={{
        /* The tone reaches the card, faintly, the way `StatCard` does it —
           so the three columns are distinguishable before any text is read. */
        background: `radial-gradient(120% 100% at 0% 0%, color-mix(in srgb, ${t.fill} 10%, transparent) 0%, transparent 62%), var(--color-surface)`,
        borderColor: `color-mix(in srgb, ${t.fill} 26%, var(--color-line))`,
      }}
    >
      <div className="mb-3 flex items-center gap-2">
        <span
          aria-hidden="true"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
          style={{
            background: `color-mix(in srgb, ${t.fill} 16%, transparent)`,
            color: t.fill,
          }}
        >
          {icon}
        </span>
        <h3 className="font-dash text-dash-md font-extrabold text-ink">{title}</h3>
        <span
          className="tabular ml-auto rounded-pill px-2.5 py-0.5 font-dash text-dash-xs font-extrabold"
          style={{
            background: `color-mix(in srgb, ${t.fill} 15%, var(--color-surface))`,
            color: t.fg,
          }}
        >
          {count}
        </span>
      </div>
      {hint !== undefined && (
        <p className="mb-3 font-dash text-dash-xs text-muted">{hint}</p>
      )}
      <div className="min-h-0 flex-1">{children}</div>
      {action && (
        <div className="mt-4 border-t border-line pt-3 text-center">
          <button
            type="button"
            onClick={action.onClick}
            className="font-dash text-dash-xs font-bold text-muted transition-colors duration-150 ease-dash hover:text-ink"
          >
            {action.label}
          </button>
        </div>
      )}
    </div>
  )
}
